"use client";

import { useState, useRef, useEffect } from "react";
import { useTranslations } from "next-intl";
import { Link, usePathname } from "@/i18n/navigation";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { propertiesApi } from "@/lib/api/properties";

export function NavLinks() {
  const t = useTranslations("Navigation");
  const pathname = usePathname();

  // Properties dropdown logic (hover)
  const [propertiesOpen, setPropertiesOpen] = useState(false);
  const propertiesTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const { data: properties } = useQuery({
    queryKey: ["properties"],
    queryFn: propertiesApi.listProperties,
    staleTime: 60_000,
  });

  const propertiesActive =
    pathname === "/properties" || pathname.startsWith("/properties/");
  const hasProperties = (properties?.length ?? 0) > 0;

  const openPropertiesMenu = () => {
    clearTimeout(propertiesTimerRef.current);
    setPropertiesOpen(true);
  };
  const closePropertiesMenu = () => {
    propertiesTimerRef.current = setTimeout(() => setPropertiesOpen(false), 150);
  };

  // My Portfolio dropdown logic (click + click outside)
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Goals dropdown logic
  const [goalsOpen, setGoalsOpen] = useState(false);
  const goalsContainerRef = useRef<HTMLDivElement>(null);

  // Accounts dropdown logic
  const [accountsOpen, setAccountsOpen] = useState(false);
  const accountsContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
      if (goalsContainerRef.current && !goalsContainerRef.current.contains(event.target as Node)) {
        setGoalsOpen(false);
      }
      if (accountsContainerRef.current && !accountsContainerRef.current.contains(event.target as Node)) {
        setAccountsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isOverviewActive = pathname === "/";
  const isAccountsListActive = pathname === "/accounts" || (pathname.startsWith("/accounts/") && !pathname.startsWith("/accounts/integrations"));
  const isIntegrationsActive = pathname.startsWith("/accounts/integrations");
  const isAccountsActive = isAccountsListActive || isIntegrationsActive;
  const isAnalysisActive = pathname === "/portfolio/analysis" || pathname.startsWith("/portfolio/analysis/");
  const isPortfolioActive =
    (pathname === "/portfolio" || pathname.startsWith("/portfolio/")) && !isAnalysisActive;
  const isActivitiesActive = pathname === "/activities" || pathname.startsWith("/activities/");
  const isMyPortfolioSectionActive = isPortfolioActive || isActivitiesActive || isAnalysisActive;
  const isGoalsActive = pathname === "/goals" || pathname.startsWith("/goals/");

  return (
    <nav className="hidden md:flex items-center gap-1">
      {/* Overview */}
      <Link
        href="/"
        className={cn(
          "px-4 py-1.5 text-sm font-medium rounded-md transition-colors whitespace-nowrap",
          isOverviewActive ? "bg-surface text-text" : "text-muted hover:text-text"
        )}
      >
        {t("overview")}
      </Link>

      {/* Accounts Dropdown */}
      <div className="relative" ref={accountsContainerRef}>
        <button
          onClick={() => setAccountsOpen(!accountsOpen)}
          className={cn(
            "px-4 py-1.5 text-sm font-medium rounded-md transition-colors whitespace-nowrap flex items-center gap-1.5 cursor-pointer",
            isAccountsActive ? "bg-surface text-text" : "text-muted hover:text-text"
          )}
        >
          {t("accounts")}
          <ChevronDown className={cn("w-3.5 h-3.5 transition-transform duration-200", accountsOpen && "rotate-180")} />
        </button>

        {accountsOpen && (
          <div className="absolute left-0 mt-1.5 w-44 bg-surface border border-border rounded-lg shadow-xl overflow-hidden z-50 p-1">
            <Link
              href="/accounts"
              onClick={() => setAccountsOpen(false)}
              className={cn(
                "block px-3 py-2 text-sm rounded-md transition-colors",
                isAccountsListActive ? "bg-surface-2 text-text font-medium" : "text-muted hover:text-text hover:bg-surface-2"
              )}
            >
              {t("accountsList")}
            </Link>
            <Link
              href="/accounts/integrations"
              onClick={() => setAccountsOpen(false)}
              className={cn(
                "block px-3 py-2 text-sm rounded-md transition-colors",
                isIntegrationsActive ? "bg-surface-2 text-text font-medium" : "text-muted hover:text-text hover:bg-surface-2"
              )}
            >
              {t("integrations")}
            </Link>
          </div>
        )}
      </div>

      {/* My Portfolio Dropdown */}
      <div className="relative" ref={containerRef}>
        <button
          onClick={() => setDropdownOpen(!dropdownOpen)}
          className={cn(
            "px-4 py-1.5 text-sm font-medium rounded-md transition-colors whitespace-nowrap flex items-center gap-1.5 cursor-pointer",
            isMyPortfolioSectionActive ? "bg-surface text-text" : "text-muted hover:text-text"
          )}
        >
          {t("myPortfolio")}
          <ChevronDown className={cn("w-3.5 h-3.5 transition-transform duration-200", dropdownOpen && "rotate-180")} />
        </button>

        {dropdownOpen && (
          <div className="absolute left-0 mt-1.5 w-44 bg-surface border border-border rounded-lg shadow-xl overflow-hidden z-50 p-1">
            <Link
              href="/portfolio"
              onClick={() => setDropdownOpen(false)}
              className={cn(
                "block px-3 py-2 text-sm rounded-md transition-colors",
                isPortfolioActive ? "bg-surface-2 text-text font-medium" : "text-muted hover:text-text hover:bg-surface-2"
              )}
            >
              {t("portfolio")}
            </Link>
            <Link
              href="/portfolio/analysis"
              onClick={() => setDropdownOpen(false)}
              className={cn(
                "block px-3 py-2 text-sm rounded-md transition-colors",
                isAnalysisActive ? "bg-surface-2 text-text font-medium" : "text-muted hover:text-text hover:bg-surface-2"
              )}
            >
              {t("analysis")}
            </Link>
            <Link
              href="/activities"
              onClick={() => setDropdownOpen(false)}
              className={cn(
                "block px-3 py-2 text-sm rounded-md transition-colors",
                isActivitiesActive ? "bg-surface-2 text-text font-medium" : "text-muted hover:text-text hover:bg-surface-2"
              )}
            >
              {t("activities")}
            </Link>
          </div>
        )}
      </div>

      {/* Goals Dropdown */}
      <div className="relative" ref={goalsContainerRef}>
        <button
          onClick={() => setGoalsOpen(!goalsOpen)}
          className={cn(
            "px-4 py-1.5 text-sm font-medium rounded-md transition-colors whitespace-nowrap flex items-center gap-1.5 cursor-pointer",
            isGoalsActive ? "bg-surface text-text" : "text-muted hover:text-text"
          )}
        >
          {t("goals")}
          <ChevronDown className={cn("w-3.5 h-3.5 transition-transform duration-200", goalsOpen && "rotate-180")} />
        </button>

        {goalsOpen && (
          <div className="absolute left-0 mt-1.5 w-44 bg-surface border border-border rounded-lg shadow-xl overflow-hidden z-50 p-1">
            <Link
              href="/goals"
              onClick={() => setGoalsOpen(false)}
              className={cn(
                "block px-3 py-2 text-sm rounded-md transition-colors",
                pathname === "/goals" ? "bg-surface-2 text-text font-medium" : "text-muted hover:text-text hover:bg-surface-2"
              )}
            >
              {t("goalsList")}
            </Link>
            <Link
              href="/goals/calculadora"
              onClick={() => setGoalsOpen(false)}
              className={cn(
                "block px-3 py-2 text-sm rounded-md transition-colors",
                pathname === "/goals/calculadora" ? "bg-surface-2 text-text font-medium" : "text-muted hover:text-text hover:bg-surface-2"
              )}
            >
              {t("goalsCalculator")}
            </Link>
          </div>
        )}
      </div>

      {/* Properties with submenu (hover-based) */}
      <div
        className="relative"
        onMouseEnter={openPropertiesMenu}
        onMouseLeave={closePropertiesMenu}
      >
        <Link
          href="/properties"
          className={cn(
            "flex items-center gap-1 px-4 py-1.5 text-sm font-medium rounded-md transition-colors whitespace-nowrap",
            propertiesActive ? "bg-surface text-text" : "text-muted hover:text-text"
          )}
        >
          {t("properties")}
          {hasProperties && (
            <ChevronDown
              className={cn(
                "h-3.5 w-3.5 opacity-50 transition-transform duration-150",
                propertiesOpen && "rotate-180"
              )}
            />
          )}
        </Link>

        {propertiesOpen && hasProperties && (
          <div className="absolute top-full left-0 mt-1 min-w-[200px] bg-surface border border-border rounded-lg shadow-xl overflow-hidden z-50 py-1">
            {properties!.map((property) => {
              const active = pathname === `/properties/${property.id}`;
              return (
                <Link
                  key={property.id}
                  href={`/properties/${property.id}`}
                  onClick={() => setPropertiesOpen(false)}
                  className={cn(
                    "flex items-center px-3 py-2 text-sm transition-colors truncate",
                    active
                      ? "bg-surface-2 text-text"
                      : "text-muted hover:bg-surface-2 hover:text-text"
                  )}
                >
                  {property.name}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </nav>
  );
}
